import { createI18n } from "vue-i18n";

import english from "./locales/en.json";

import { invokeAsync } from "./helpers/async";

import { LanguageTag } from "../modules/shared-utils/languageTag";

const DEFAULT_LANGUAGE = "en";

const messages: Record<string, typeof english> = { en: english };

type LocaleFetcher = () => Promise<typeof english>;

const supportedLocales: Record<string, LocaleFetcher> = Object.fromEntries([
  // already loaded English messages
  ["en", () => Promise.resolve(english)],
  // other locales loaded dynamically
  ...Object.entries(
    import.meta.glob(["./locales/*.json", "!./locales/en.json"], {
      import: "default",
    })
  ).map(([path, fetcher]) => [
    path.match(/\/([a-z-]+)\.json$/)![1],
    fetcher as LocaleFetcher,
  ]),
]);

const i18n = createI18n({
  locale: DEFAULT_LANGUAGE,
  fallbackLocale: DEFAULT_LANGUAGE, // set fallback locale
  messages, // set locale messages
  legacy: false,
});

let requestedLocale: string | null = null;
export async function setLocale(locale: string | null): Promise<void> {
  requestedLocale = locale;

  const browserLocales = navigator.languages || [navigator.language];

  const selectedLocale = findSupportedLocale(
    requestedLocale,
    ...browserLocales
  );

  if (!i18n.global.availableLocales.includes(selectedLocale)) {
    const localeData = await supportedLocales[selectedLocale]();
    i18n.global.setLocaleMessage(selectedLocale, localeData);
  }

  document.documentElement.setAttribute("lang", selectedLocale);
  i18n.global.locale.value = selectedLocale;
}

window.addEventListener("languagechange", () =>
  invokeAsync(() => setLocale(requestedLocale))
);

function findSupportedLocale(...locales: (string | null)[]): string {
  for (let locale of locales) {
    if (!locale) continue;

    if (Object.hasOwn(supportedLocales, locale)) {
      return locale;
    }

    locale = LanguageTag.parse(locale).primary;
    if (Object.hasOwn(supportedLocales, locale)) {
      return locale;
    }
  }
  return DEFAULT_LANGUAGE;
}

export default i18n;
